/**
 * Inventory schema — equipment slots + backpack.
 *
 * Lives in `shared/` so server (equip checks, stat aggregation, drop-on-death)
 * and client (HUD, drag-and-drop UI) read identical Schema shapes. The 3
 * equipment slots and 8 backpack slots are pre-seeded by `initCubeInventory`
 * the moment a Cube is constructed, so consumers can iterate the maps without
 * worrying about lazy creation. Empty slots carry `itemId === ""`.
 *
 * Why MapSchema (not flat fields):
 *   - @colyseus/schema caps each Schema class at 64 fields. ResourceBag
 *     already groups 17 counters; equipment + backpack would add 11 more flat
 *     fields and overflow Cube. Two MapSchemas cost 2 fields total.
 *   - Maps make slot count tweaks data-driven (BACKPACK_SLOT_COUNT below).
 *   - Wire deltas for "moved a stone from slot 3 to slot 7" stay tight: only
 *     the two changed entries serialize.
 *
 * Slot keys are stable and human-readable:
 *   - Equipment: "main-hand" | "offhand" | "armor" — matches ItemSlot enum
 *     values from items.ts so equip code can dispatch by slot string with no
 *     mapping table.
 *   - Backpack:  "0" .. "11" — 12 slots, per the v10 UI spec
 *     (`docs/superpowers/specs/2026-05-01-ui-implementation-spec.md` §1).
 *     Was 8 ("0".."7", matching KTS Inventory size) until 2026-05-03; bumped
 *     to 6×2 = 12 alongside the resources-as-inventory migration so lumber +
 *     stone + the rest of the former scalar bag can occupy slots without
 *     starving real items.
 *
 * EquipSlot vs BackpackSlot:
 *   - EquipSlot is a single item reference (stack count is meaningless for
 *     equipped weapons / armor in v1).
 *   - BackpackSlot carries `itemId` + `count` so stackable consumables (food,
 *     potions, raw materials someday) can occupy a single slot.
 */
import { Schema, type, MapSchema } from "@colyseus/schema";

/** A slot that holds at most one equipped item. count is implicit (1 or 0). */
export class EquipSlot extends Schema {
  @type("string") itemId: string = "";
}

/** A backpack slot — stackable. itemId="" + count=0 means empty. */
export class BackpackSlot extends Schema {
  @type("string") itemId: string = "";
  @type("uint16") count: number = 0;
  /** B5 Happy Chef provenance: the Cooking tier of the chef who cooked this food
   *  stack (0 = not cooked / non-food). Eating a stack with cookedByTier >= 5
   *  grants the Happy Chef buff. Stacks only merge when this matches (so t5-cooked
   *  food never merges with looted/lower-tier food). Stale values on an EMPTY slot
   *  are never read (every reader guards on itemId !== ""), so no reset-on-clear. */
  @type("uint8") cookedByTier: number = 0;
}

/**
 * Equipment slot keys, in display order. The strings match ItemSlot values
 * from items.ts so callers can do `cube.equipment.get(item.slot)`.
 */
export const EQUIPMENT_SLOT_KEYS = ["main-hand", "offhand", "armor"] as const;
export type EquipmentSlotKey = (typeof EQUIPMENT_SLOT_KEYS)[number];

/** v1 backpack size — 6×2 = 12 per the v10 UI spec. KTS Inventory cap was 8;
 *  v10 expands to 12 so lumber/stone/etc. (resource-policy-routed materials)
 *  can occupy slots alongside equipment + consumables without starving the
 *  player's real-item space. The schema cost is one extra MapSchema entry
 *  per cube — backpack is a single @colyseus/schema field regardless of
 *  size, so the 64-field cap on Cube is unaffected. */
export const BACKPACK_SLOT_COUNT = 12;

/**
 * Pre-seed a Cube's equipment + backpack maps so every slot exists at
 * construct time. MUST be called immediately after `new Cube()` on the
 * server (HelloRoom onJoin, dummy spawner, bot spawner, debug action).
 *
 * Cube-side declaration: `equipment: MapSchema<EquipSlot>` and
 * `backpack: MapSchema<BackpackSlot>` exist on the schema; this helper
 * just populates them so client and stat-aggregation code can iterate
 * without `.has()` guards.
 *
 * Re-running on the same Cube is harmless — it overwrites empty slots with
 * fresh empty slots — but is wasted work; call once.
 */
export function initCubeInventory(c: {
  equipment: MapSchema<EquipSlot>;
  backpack: MapSchema<BackpackSlot>;
}): void {
  for (const k of EQUIPMENT_SLOT_KEYS) {
    c.equipment.set(k, new EquipSlot());
  }
  for (let i = 0; i < BACKPACK_SLOT_COUNT; i++) {
    c.backpack.set(String(i), new BackpackSlot());
  }
}
